import { type Mesh } from '@/gfx/mesh';

export const createCylinderMesh = (
  radiusBottom: number,
  radiusTop: number,
  height: number,
  segments = 6,
): Mesh => {
  if (segments < 3) throw new Error('cylinder requires at least 3 segments');
  // Each side face is a quad (4 unique verts for flat shading) → 2 triangles.
  const vertCount = segments * 4;
  const positions = new Float32Array(vertCount * 3);
  const normals = new Float32Array(vertCount * 3);
  const uvs = new Float32Array(vertCount * 2);
  const indices = new Uint16Array(segments * 6);

  for (let s = 0; s < segments; s++) {
    const a0 = (s / segments) * Math.PI * 2;
    const a1 = ((s + 1) / segments) * Math.PI * 2;
    const c0 = Math.cos(a0);
    const s0 = Math.sin(a0);
    const c1 = Math.cos(a1);
    const s1 = Math.sin(a1);

    const x0b = c0 * radiusBottom;
    const z0b = s0 * radiusBottom;
    const x1b = c1 * radiusBottom;
    const z1b = s1 * radiusBottom;
    const x0t = c0 * radiusTop;
    const z0t = s0 * radiusTop;
    const x1t = c1 * radiusTop;
    const z1t = s1 * radiusTop;

    // Outward normal: average direction along the face midpoint.
    const mx = (c0 + c1) * 0.5;
    const mz = (s0 + s1) * 0.5;
    const dr = radiusBottom - radiusTop; // taper: pushes normal up if top is narrower
    // Tilt component (in Y) proportional to dr/height when faces lean inward.
    const tiltY = dr / Math.hypot(height, dr);
    const horizScale = height / Math.hypot(height, dr);
    let nx = mx * horizScale;
    let ny = tiltY;
    let nz = mz * horizScale;
    const inv = 1 / Math.hypot(nx, ny, nz);
    nx *= inv;
    ny *= inv;
    nz *= inv;

    const base = s * 4;
    // bottom-left (a0, bottom), bottom-right (a1, bottom), top-right (a1, top), top-left (a0, top)
    const verts: ReadonlyArray<readonly [number, number, number]> = [
      [x0b, 0, z0b],
      [x1b, 0, z1b],
      [x1t, height, z1t],
      [x0t, height, z0t],
    ];
    for (let k = 0; k < 4; k++) {
      const p = verts[k]!;
      const pi = (base + k) * 3;
      positions[pi] = p[0];
      positions[pi + 1] = p[1];
      positions[pi + 2] = p[2];
      normals[pi] = nx;
      normals[pi + 1] = ny;
      normals[pi + 2] = nz;
    }
    const ui = base * 2;
    uvs[ui] = s / segments;
    uvs[ui + 1] = 1;
    uvs[ui + 2] = (s + 1) / segments;
    uvs[ui + 3] = 1;
    uvs[ui + 4] = (s + 1) / segments;
    uvs[ui + 5] = 0;
    uvs[ui + 6] = s / segments;
    uvs[ui + 7] = 0;

    const ii = s * 6;
    // CCW from outside: bottom-left, top-right, bottom-right; then bottom-left, top-left, top-right.
    indices[ii] = base;
    indices[ii + 1] = base + 2;
    indices[ii + 2] = base + 1;
    indices[ii + 3] = base;
    indices[ii + 4] = base + 3;
    indices[ii + 5] = base + 2;
  }

  return { positions, normals, uvs, indices };
};
